import { DefaultButton, PrimaryButton } from "@fluentui/react";
import { CSSProperties, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { submitSignatureQueue } from "src/redux/actions/qualityHubActions";
import { UnstyledButton } from "src/ui";
import SpintrUser from "src/ui/components/SpintrUser";
import { Portal } from "../Portal";
import { CaptionBody, ErrorText, ExtraLargeHeading, LargeBody } from "../Text";
import {
    PinCodeModalProps,
    PinCodeSelectableUser,
    PinCodeModalState,
    PinCodeModalStateProps,
} from "./PinCodeModal.types";
import { queryQualityHubPersonnelAsync } from "src/api";
import SpintrLoader from "src/ui/components/Loader";
import { localize, localizeFormat } from "src/l10n";

function isStatePropsUnchanged(
    prev: PinCodeModalStateProps,
    next: PinCodeModalStateProps,
): boolean {
    return prev.currentUser.id === next.currentUser.id
        && prev.isSmallViewMode === next.isSmallViewMode
        && prev.isSubmitting === next.isSubmitting
        && prev.lastSubmitError === next.lastSubmitError
        && prev.signatureQueueSize === next.signatureQueueSize
        && prev.isHighContrastMode === next.isHighContrastMode;
}

const pinLength = 4;
const pinIndices = Array.from({ length: pinLength }, (_, index) => index);

function PinCodeModal({
    onDismiss,
    enableUserSelection,
    localValidation,
}: PinCodeModalProps): JSX.Element | null {
    const {
        currentUser,
        isSmallViewMode,
        isSubmitting,
        lastSubmitError,
        signatureQueueSize,
    } = useSelector<Spintr.AppState, PinCodeModalStateProps>(
        (appState) => ({
            currentUser: appState.profile.active,
            isSmallViewMode: appState.ui.isSmallViewMode,
            isHighContrastMode: appState.ui.colorMode == "contrast-dark",
            isSubmitting: appState.qualityHub.signatureQueue.isSubmitting,
            lastSubmitError: appState.qualityHub.signatureQueue.lastSubmitError,
            signatureQueueSize: appState.qualityHub.signatureQueue.operations.length,
        }),
        isStatePropsUnchanged
    );

    const dispatch = useDispatch();
    const pinInputRef = useRef<HTMLInputElement>(null);
    const [state, setState] = useState<PinCodeModalState>({
        pinCode: "",
        selectedUserId: enableUserSelection ? null : currentUser.id,
    });
    const [users, setUsers] = useState<PinCodeSelectableUser[]>([]);
    const [loadingUsers, setLoadingUsers] = useState(!!enableUserSelection);
    const [error, setError] = useState<string | undefined>();
    const [keyboardInset, setKeyboardInset] = useState(0);

    useEffect(() => {
        if (!enableUserSelection) {
            return;
        }

        const ctrl = new AbortController();
        queryQualityHubPersonnelAsync(ctrl.signal)
            .then((result) => {
                if (ctrl.signal.aborted) {
                    return;
                }

                setUsers(result.map((user) => ({
                    id: user.id,
                    name: user.name,
                    role: user.title.join(", "),
                    imageUrl: user.image,
                })));
            })
            .catch(() => {
                if (ctrl.signal.aborted) {
                    return;
                }

                setError(localize("TeknisktFel"));
            })
            .finally(() => {
                if (ctrl.signal.aborted) {
                    return;
                }

                setLoadingUsers(false);
            });

        return () => {
            ctrl.abort();
        };
    }, [enableUserSelection]);

    const canSelectUser = !!enableUserSelection && state.selectedUserId === null;
    const canSubmit = state.pinCode.length === pinLength && state.selectedUserId !== null && !isSubmitting;

    useEffect(() => {
        if (canSelectUser) {
            return;
        }

        pinInputRef.current?.focus();
    }, [canSelectUser]);

    useEffect(() => {
        if (!isSmallViewMode) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setKeyboardInset(0);
            return;
        }

        const viewport = window.visualViewport;
        if (!viewport) {
            return;
        }

        const onViewportChanged = () => {
            const nextInset = Math.max(
                0,
                Math.round(window.innerHeight - viewport.height - viewport.offsetTop),
            );

            setKeyboardInset(nextInset);
        };

        onViewportChanged();
        viewport.addEventListener("resize", onViewportChanged);
        viewport.addEventListener("scroll", onViewportChanged);
        window.addEventListener("resize", onViewportChanged);

        return () => {
            viewport.removeEventListener("resize", onViewportChanged);
            viewport.removeEventListener("scroll", onViewportChanged);
            window.removeEventListener("resize", onViewportChanged);
        };
    }, [isSmallViewMode]);

    const onCloseClicked = useCallback(() => {
        onDismiss(false);
    }, [onDismiss]);

    const onSelectUser = useCallback((userId: number) => {
        setState((prevState) => ({
            ...prevState,
            selectedUserId: userId,
            pinCode: "",
        }));
        setError(undefined);
    }, []);

    const onBackToSelection = useCallback(() => {
        setState((prevState) => ({
            ...prevState,
            selectedUserId: null,
            pinCode: "",
        }));
        setError(undefined);
    }, []);

    const onPinInputChanged = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const digitsOnly = event.target.value.replace(/\D/g, "").slice(0, pinLength);

        setState((prevState) => ({
            ...prevState,
            pinCode: digitsOnly,
        }));
        setError(undefined);
    }, []);

    const onSignClicked = useCallback(async () => {
        if (state.selectedUserId === null) {
            setError(localize("ValjAnvandareIFaltetOvan"));
            return;
        }

        if (state.pinCode.length !== pinLength) {
            setError(localize("QH_PIN_INSTRUCTIONS"));
            return;
        }

        try {
            if (localValidation?.enabled) {
                const userHash = localValidation.localHashes[state.selectedUserId];

                if (!userHash) {
                    setError(localize("QH_NO_PIN_AVAILABLE"));
                    return;
                }

                const enteredHash = await localValidation.createHash(
                    state.selectedUserId,
                    state.pinCode,
                );

                if (enteredHash.toLowerCase() !== userHash.toLowerCase()) {
                    setError(localize("QH_INVALID_PIN"));
                    return;
                }
            }

            const result = await (dispatch(
                submitSignatureQueue(state.pinCode, state.selectedUserId),
            ) as unknown as Promise<boolean>);

            if (result) {
                onDismiss(true);
            }
        } catch {
            setError(localize("TeknisktFelProvaIgenEllerKontaktaAdministrator"));
        }
    }, [dispatch, localValidation, onDismiss, state.pinCode, state.selectedUserId]);

    const modalError = error || lastSubmitError;
    const modalStyle = isSmallViewMode
        ? { "--pin-code-modal-keyboard-inset": `${keyboardInset}px` } as CSSProperties
        : undefined;

    return (
        <Portal>
            <div className={`PinCodeModal ${isSmallViewMode ? "small" : "desktop"}`} style={modalStyle}>
                <div className="PinCodeModal-overlay" />
                <div className="PinCodeModal-shell">

                    <div className="PinCodeModal-content">
                        {canSelectUser ? (
                            <div className="PinCodeModal-userSelection">
                                {loadingUsers ? <SpintrLoader /> : users.map((user) => (
                                    <div className="PinCodeModal-userRow" key={user.id}>
                                        <SpintrUser
                                            hideImage={false}
                                            hideText={false}
                                            imageUrl={user.imageUrl}
                                            name={user.name}
                                            personalName={true}
                                            subText={user.role}
                                        />
                                        <DefaultButton
                                            className="PinCodeModal-selectButton"
                                            onClick={() => onSelectUser(user.id)}
                                            text="Select"
                                        />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="PinCodeModal-pinStep">
                                <LargeBody className="PinCodeModal-pinHeading"
                                           color="contentDark"
                                           weight="semiBold">
                                    {localizeFormat("QH_SIGN_X_TASKS", signatureQueueSize)}
                                </LargeBody>
                                <CaptionBody className="PinCodeModal-pinSubHeading">
                                    {localize("QH_PIN_INSTRUCTIONS")}
                                </CaptionBody>
                                <div className="PinCodeModal-pinBoxes">
                                    <input
                                        className="PinCodeModal-hiddenInput"
                                        maxLength={pinLength}
                                        onChange={onPinInputChanged}
                                        ref={pinInputRef}
                                        type="password"
                                        value={state.pinCode}
                                    />
                                    {pinIndices.map((index) => (
                                        <ExtraLargeHeading
                                            className="PinCodeModal-pinBox"
                                             color="black"
                                             role="button"
                                             key={index}
                                             onKeyDown={((ev) => {
                                                if (ev.key === "Enter" || ev.key === " ") {
                                                    ev.preventDefault();
                                                    pinInputRef.current?.focus?.();
                                                }
                                             })}
                                             onClick={() => pinInputRef.current?.focus?.()}
                                             tabIndex={-1}
                                        >
                                            {state.pinCode[index] ? "*" : "_"}
                                        </ExtraLargeHeading>
                                    ))}
                                </div>

                                {modalError && (
                                    <ErrorText className="PinCodeModal-errorText">
                                        {modalError}
                                    </ErrorText>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="PinCodeModal-actions">
                        <PrimaryButton
                            className="PinCodeModal-submitButton"
                            disabled={!canSubmit}
                            onClick={onSignClicked}
                            text={localize("QH_SIGN_TASKS")}
                        />
                        <UnstyledButton
                            className="PinCodeModal-cancelButton"
                            onClick={canSelectUser ? onCloseClicked : (enableUserSelection ? onBackToSelection : onCloseClicked)}
                        >
                            {localize("Avbryt")}
                        </UnstyledButton>
                    </div>
                </div>
            </div>
        </Portal>
    );
}

export default PinCodeModal;
